// Division-office desks. An application moves through three stages, and in
// each stage it visits the Section Clerk, the Superintendent and the DFO in
// turn; the backend tracks this as `task` T1..T10 (T7 is with the range
// officers). Used by ApplicationList (pending desk), RoleDashboard and
// ApplicationDetail.

export const STAGES = [
  { stage: 1, name: "New applications", short: "New", icon: "mdi-file-document-edit-outline" },
  { stage: 2, name: "After fee payment", short: "Paid", icon: "mdi-cash-check" },
  { stage: 3, name: "After range mahazar", short: "Mahazar", icon: "mdi-map-marker-check-outline" },
];

export const desks = {
  clerk: {
    title: "Section Clerk Desk",
    detailRoute: "/CampDetailedViewClerk",
    listRoute: "/sectionhome",
    tasks: {
      T1: "Enter the film shooting fees, add remarks and forward the application to the Superintendent.",
      T4: "Upload the executed agreement, add remarks and forward the application to the Superintendent.",
      T8: "Add remarks and forward the application to the Superintendent.",
    },
  },
  superintendent: {
    title: "Superintendent Desk",
    detailRoute: "/DetailedViewsuperintedent",
    listRoute: "/superintedent",
    tasks: {
      T2: "Update the fees if needed, add remarks, then forward to the DFO or send back to the Clerk for clarification.",
      T5: "Add remarks, then forward to the DFO or send back to the Clerk for clarification.",
      T9: "Update the refund amount if needed, add remarks, then forward to the DFO or send back to the Clerk.",
    },
  },
  division: {
    title: "DFO Desk",
    detailRoute: "/FilmDetailedView",
    listRoute: "/ApplicationDivision",
    tasks: {
      T3: "Update the fees if needed, return to the applicant or Superintendent, reject, or proceed to fee payment.",
      T6: "Select the range offices, generate the licence, proceedings and letter to the RFO, and forward to the ranges.",
      T10: "Send back to the range offices for clarification, update the refund amount and approve the refund.",
    },
  },
  // Range offices work on one step only: the site inspection the DFO sends
  // them to (T7), between stage 2 and stage 3.
  range: {
    title: "Range Office Desk",
    detailRoute: "/CampDetailedView",
    listRoute: "/rangebookings",
    stages: [{ stage: 1, name: "Site inspection", short: "Inspection", icon: "mdi-map-marker-radius-outline", tag: "Mahazar & report · T7" }],
    tasks: {
      T7: "Inspect the shooting locations and upload your mahazar and report. Once every range office has uploaded, any of them can forward it to the Section Clerk.",
    },
  },
};

// [{ stage, task, ...STAGES[i], hint }] for a desk role. `tag` is the short
// line under the stage name ("Stage 1 · T1" unless the desk sets its own).
export function stagesFor(role) {
  const desk = desks[role];
  if (!desk) return [];
  return Object.keys(desk.tasks).map((task, i) => {
    const meta = (desk.stages && desk.stages[i]) || STAGES[i];
    return { tag: "Stage " + meta.stage + " · " + task, ...meta, task, hint: desk.tasks[task] };
  });
}

// Plain-language label for where an application is in the workflow.
export const TASK_LABELS = {
  T0: "Returned to applicant",
  T1: "With Section Clerk",
  T2: "With Superintendent",
  T3: "With DFO",
  T4: "With Section Clerk (paid)",
  T5: "With Superintendent (paid)",
  T6: "With DFO (paid)",
  T7: "With Range Officers",
  T8: "With Section Clerk (mahazar)",
  T9: "With Superintendent (mahazar)",
  T10: "With DFO (mahazar)",
};

// Role of a user by uType, as shown on lists and timelines.
export const ROLE_NAMES = {
  780: "Super Admin",
  777: "Admin",
  775: "Circle",
  774: "DFO",
  771: "DFO",
  7741: "Section Clerk",
  7742: "Superintendent",
  773: "Range Officer",
  772: "Applicant",
};

// Who holds an application now. While it waits at a desk any officer in that
// role can take it up, so the desk is shown rather than one officer's name.
export function currentlyWith(app) {
  const item = app || {};
  if (item.status && item.status !== "Active") {
    return { who: "No one — closed", detail: item.status, stage: "", icon: "mdi-lock-outline", tone: "closed" };
  }
  if (item.isforwardtoRange === "yes" || item.task === "T7") {
    const ranges = item.rangeids || [];
    // Office names when the list populated them, otherwise just how many.
    const names = ranges.map((r) => r && (r.officeName || r.username)).filter(Boolean);
    const n = ranges.length;
    const detail = names.length ? names.join(", ") : n ? n + " range office" + (n > 1 ? "s" : "") : "";
    return { who: "Range Officers", detail, stage: TASK_LABELS.T7, icon: "mdi-pine-tree", tone: "range" };
  }
  if (item.task === "T0") {
    return { who: item.applicantName || "Applicant", detail: "Applicant", stage: TASK_LABELS.T0, icon: "mdi-account-outline", tone: "applicant" };
  }
  const holder = item.forwardto || {};
  const role = ROLE_NAMES[Number(holder.uType)] || "";
  return {
    who: role ? role + " desk" : TASK_LABELS[item.task] || "—",
    detail: "Waiting to be processed",
    stage: TASK_LABELS[item.task] || "",
    icon: "mdi-office-building-outline",
    tone: "office",
  };
}

// One-line version of currentlyWith, e.g. "With Superintendent desk".
export function currentlyWithLabel(app) {
  if (!app || !(app._id || app.applicationNo)) return "";
  const w = currentlyWith(app);
  if (w.tone === "closed") return "Closed · " + w.detail;
  if (w.tone === "applicant") return TASK_LABELS.T0;
  if (w.tone === "range") return TASK_LABELS.T7;
  return w.who.startsWith("With") ? w.who : "With " + w.who;
}

// Status pill for an application, matching the wording used across the app.
export function statusOf(app) {
  if (!app) return { label: "—", tone: "neutral" };
  if (app.status === "Rejected") return { label: "Rejected", tone: "danger" };
  if (app.status === "Cancelled") {
    // A paid application that was cancelled is closed only once the refund is paid.
    if (app.paymentStatus === "Success") {
      return app.refundStatus === "Completed"
        ? { label: "Cancelled · Refunded", tone: "success" }
        : { label: "Cancelled · Refund pending", tone: "warning" };
    }
    return { label: "Cancelled", tone: "neutral" };
  }
  if (app.status === "Cancelrequested") return { label: "Cancel requested", tone: "warning" };
  // Refund paid (e.g. the security deposit of an application that is still running).
  if (app.refundStatus === "Completed") return { label: "Refunded", tone: "success" };
  if (app.task === "T0") return { label: "Returned to applicant", tone: "warning" };
  if (app.paymentStatus === "Success") return { label: "Paid", tone: "success" };
  if (app.paymentInitiation === "YES") return { label: "Awaiting payment", tone: "warning" };
  return { label: "Submitted", tone: "info" };
}
